import React, { useState, useCallback, useEffect, useRef } from 'react'
import { useHistory, useLocation } from 'react-router-dom'
import { useQuery, useMutation, NetworkStatus } from '@apollo/client'
import {
  Table,
  Button,
  Row,
  Col,
  Collapse,
  List,
  Typography,
  Popover,
  Dropdown,
  Menu,
  Modal,
  notification,
} from 'antd'
import {
  RetweetOutlined,
  ExceptionOutlined,
  RedoOutlined,
  LoadingOutlined,
} from '@ant-design/icons'
import { filter, fromPairs, map, omit } from 'lodash-es'
import { Panel } from '../../../core/components/styled'
import {
  createUrl,
  getQueryParams,
} from '../../../core/utils/services/queryParams'
import { formatDate } from '../../../core/utils/services/formatter'
import { timeBetween } from '../../../core/utils/services/date-utils'
import { DEFAULT_PAGE, DEFAULT_PAGE_SIZE } from '../../../common/constants'
import {
  useDocumentTitle,
  useDocumentVisibility,
} from '../../../common/hooks/effects'
import {
  formatMetricPercent,
  formatScrapingTags,
  canValidateScrapingSession,
  getValidationConfirmationContent,
} from '../../utils'
import {
  SCRAPING_SESSIONS,
  SCRAPING_SESSION_VALIDATION_STATES,
} from '../../graphql/queries'

import {
  UsersFilter,
  ScrapingSessionStatusFilter,
  ScrapingTypeFilter,
  ScrapingSessionStatus,
  ScrapingSessionValidationStatus,
  ScrapingSessionValidationStatusFilter,
  ScrapingSessionLogsModal,
  ScrapingSessionSettingsJsonView,
  renderValidationMetric,
} from './sections'
import {
  START_SCRAPING_SESSION_DUPLICATE,
  VALIDATE_SCRAPING_SESSION,
} from '../../graphql/mutations'
import {
  HTML_AUTOMATIC,
  IN_PROGRESS,
  SCHEDULED,
  SCRAPING_SESSION_VALIDATION_ERROR,
  SCRAPING_SESSION_VALIDATION_IN_PROGRESS,
  SCRAPING_SESSION_VALIDATION_POLL_INTERVAL_MS,
  SCRAPING_TYPES,
} from '../../constants'

const isValidationInProgress = scrapingSession =>
  scrapingSession.validationStatus === SCRAPING_SESSION_VALIDATION_IN_PROGRESS

const ScrapingSessionsHistoryPage = () => {
  useDocumentTitle('Scraping sessions history')

  const [logsModalSettings, setLogsModalSettings] = useState({
    sessionId: undefined,
    menuId: undefined,
    isModalOpen: false,
  })

  const [
    startingScrapingSessionForId,
    setStartingScrapingSessionForId,
  ] = useState()
  const [
    validatingScrapingSessionIds,
    setValidatingScrapingSessionIds,
  ] = useState([])

  const history = useHistory()
  const { pathname, search } = useLocation()
  const {
    userIds,
    status,
    validationStatus,
    scrapingType,
    page = DEFAULT_PAGE,
  } = getQueryParams(search)

  const isPageVisible = useDocumentVisibility()
  const [isRefreshingSessions, setIsRefreshingSessions] = useState(false)
  // Sessions with a running validation; only their status is polled.
  const [watchedValidationIds, setWatchedValidationIds] = useState([])
  // Polled validation statuses, newer than the list until it is refetched.
  const [validationStatusById, setValidationStatusById] = useState({})
  // Validations started from this page, for completion notifications.
  const startedValidationIds = useRef(new Set())

  const {
    data: { scrapingSessions = {} } = {},
    loading,
    networkStatus,
    refetch: refetchScrapingSessions,
  } = useQuery(SCRAPING_SESSIONS, {
    variables: {
      filters: { userIds, status, validationStatus, scrapingType },
      page,
    },
    fetchPolicy: 'no-cache',
    notifyOnNetworkStatusChange: true,
  })
  // Background refetches update rows in place without a spinner.
  const isLoadingSessions =
    isRefreshingSessions ||
    (loading &&
      [NetworkStatus.loading, NetworkStatus.setVariables].includes(
        networkStatus,
      ))

  const {
    data: { scrapingSessionValidationStates: validationStates } = {},
  } = useQuery(SCRAPING_SESSION_VALIDATION_STATES, {
    variables: { ids: watchedValidationIds },
    fetchPolicy: 'no-cache',
    skip: !watchedValidationIds.length || !isPageVisible,
    pollInterval: SCRAPING_SESSION_VALIDATION_POLL_INTERVAL_MS,
  })

  const watchValidations = ids =>
    setWatchedValidationIds(watchedIds => {
      const newIds = ids.filter(id => !watchedIds.includes(id))
      return newIds.length ? [...watchedIds, ...newIds] : watchedIds
    })

  useEffect(() => {
    watchValidations(
      filter(scrapingSessions.scrapingSessions, isValidationInProgress).map(
        ({ id }) => id,
      ),
    )
  }, [scrapingSessions])

  useEffect(() => {
    if (!validationStates) return

    setValidationStatusById(statuses => ({
      ...statuses,
      ...fromPairs(
        validationStates.map(({ id, validationStatus }) => [
          id,
          validationStatus,
        ]),
      ),
    }))

    const finishedStates = validationStates.filter(
      state => !isValidationInProgress(state),
    )
    if (!finishedStates.length) return

    const finishedIds = finishedStates.map(({ id }) => id)
    setWatchedValidationIds(watchedIds =>
      watchedIds.filter(id => !finishedIds.includes(id)),
    )
    finishedStates
      .filter(({ id }) => startedValidationIds.current.has(id))
      .forEach(({ id, validationStatus: finishedStatus }) => {
        startedValidationIds.current.delete(id)
        notification.open(
          finishedStatus === SCRAPING_SESSION_VALIDATION_ERROR
            ? {
                message: `Validation failed for session ${id}.`,
                type: 'error',
                duration: 0,
              }
            : {
                message: `Validation completed for session ${id}.`,
                type: 'success',
              },
        )
      })

    // Load the new scores, then let the list own these rows again.
    refetchScrapingSessions()
      .catch(() => undefined)
      .then(() =>
        setValidationStatusById(statuses => omit(statuses, finishedIds)),
      )
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [validationStates])

  const tableSessions = map(scrapingSessions.scrapingSessions, session => ({
    ...session,
    validationStatus: validatingScrapingSessionIds.includes(session.id)
      ? SCRAPING_SESSION_VALIDATION_IN_PROGRESS
      : validationStatusById[session.id] || session.validationStatus,
  }))

  const refreshScrapingSessions = async () => {
    setIsRefreshingSessions(true)
    try {
      await refetchScrapingSessions()
    } finally {
      setIsRefreshingSessions(false)
    }
  }

  const [startScrapingSessionDuplicate] = useMutation(
    START_SCRAPING_SESSION_DUPLICATE,
    {
      onCompleted() {
        refetchScrapingSessions()
        setStartingScrapingSessionForId()
        notification.open({
          message: 'Started scraping session.',
          type: 'success',
        })
      },
      onError({ message }) {
        notification.open({
          message: 'Failed to start scraping session.',
          description: message,
          type: 'error',
          duration: 0,
        })
      },
    },
  )

  const [validateScrapingSession] = useMutation(VALIDATE_SCRAPING_SESSION)

  const validateScrapingSessionById = async scrapingSessionId => {
    setValidatingScrapingSessionIds(validatingIds => [
      ...validatingIds,
      scrapingSessionId,
    ])

    try {
      const { data } = await validateScrapingSession({
        variables: { id: scrapingSessionId },
      })
      startedValidationIds.current.add(scrapingSessionId)
      setValidationStatusById(statuses => ({
        ...statuses,
        [scrapingSessionId]: data.validateScrapingSession.validationStatus,
      }))
      watchValidations([scrapingSessionId])
      notification.open({
        message: 'Session validation started.',
        type: 'info',
      })
    } catch ({ message }) {
      notification.open({
        message: 'Failed to validate scraping session.',
        description: message,
        type: 'error',
        duration: 0,
      })
    } finally {
      setValidatingScrapingSessionIds(validatingIds =>
        validatingIds.filter(id => id !== scrapingSessionId),
      )
    }
  }

  const startScrapingSessionWithDefaultModeScraping = useCallback(
    scrapingSessionId => {
      setStartingScrapingSessionForId(scrapingSessionId)

      startScrapingSessionDuplicate({
        variables: {
          id: scrapingSessionId,
          isDebugModeScraping: false,
        },
      })
    },
    [startScrapingSessionDuplicate],
  )

  const startScrapingSessionWithDebugModeScraping = useCallback(
    scrapingSessionId => {
      setStartingScrapingSessionForId(scrapingSessionId)

      startScrapingSessionDuplicate({
        variables: {
          id: scrapingSessionId,
          isDebugModeScraping: true,
        },
      })
    },
    [startScrapingSessionDuplicate],
  )

  const rerunButtonMenu = scrapingSession => (
    <Menu
      onClick={() =>
        startScrapingSessionWithDebugModeScraping(scrapingSession.id)
      }
    >
      <Menu.Item>Rerun in debug mode</Menu.Item>
    </Menu>
  )

  const isSessionRunning = scrapingSession =>
    scrapingSession.status === IN_PROGRESS ||
    scrapingSession.status === SCHEDULED

  const renderRerunButton = scrapingSession => {
    if (scrapingSession.id === startingScrapingSessionForId) {
      return <Button style={{ width: '75px' }} icon={<LoadingOutlined />} />
    }

    if (scrapingSession.scrapingType === HTML_AUTOMATIC) {
      return (
        <Button
          style={{ width: '75px' }}
          disabled={isSessionRunning(scrapingSession)}
          icon={<RedoOutlined />}
          onClick={() =>
            startScrapingSessionWithDefaultModeScraping(scrapingSession.id)
          }
        />
      )
    }

    return (
      <Dropdown.Button
        style={{ width: '75px' }}
        disabled={isSessionRunning(scrapingSession)}
        onClick={() =>
          startScrapingSessionWithDefaultModeScraping(scrapingSession.id)
        }
        overlay={() => rerunButtonMenu(scrapingSession)}
      >
        <RedoOutlined />
      </Dropdown.Button>
    )
  }

  const handleValidateScrapingSession = async (event, scrapingSession) => {
    event.stopPropagation()

    let refreshedSession = scrapingSession
    if (scrapingSession.status === IN_PROGRESS) {
      try {
        const { data } = await refetchScrapingSessions()
        refreshedSession = data?.scrapingSessions?.scrapingSessions?.find(
          session => session.id === scrapingSession.id,
        )
      } catch ({ message }) {
        notification.open({
          message: 'Failed to refresh scraping session.',
          description: message,
          type: 'error',
          duration: 0,
        })
        return
      }
    }

    if (!refreshedSession || !canValidateScrapingSession(refreshedSession)) {
      notification.open({
        message: 'No menus are currently available for validation.',
        type: 'warning',
      })
      return
    }

    Modal.confirm({
      title: 'Validate scraping session?',
      content: getValidationConfirmationContent(refreshedSession),
      okText: 'Validate',
      onOk: () => {
        validateScrapingSessionById(refreshedSession.id)
      },
    })
  }

  const tableColumns = [
    {
      title: 'Status',
      dataIndex: 'status',
      width: '120px',
      render: status => <ScrapingSessionStatus status={status} />,
    },
    {
      title: 'Validation status',
      dataIndex: 'validationStatus',
      width: '135px',
      render: (status, scrapingSession) => (
        <ScrapingSessionValidationStatus
          status={status}
          scrapingStatus={scrapingSession.status}
        />
      ),
    },
    {
      title: 'Validation score',
      width: '120px',
      render: scrapingSession =>
        renderValidationMetric(
          formatMetricPercent(
            scrapingSession.validationMetrics?.validationScore,
          ),
        ),
    },
    {
      title: 'Session average total similarity',
      width: '180px',
      render: scrapingSession =>
        renderValidationMetric(
          formatMetricPercent(
            scrapingSession.validationMetrics?.totalSimilarity,
          ),
        ),
    },
    {
      title: 'Progress',
      width: '50px',
      render: scrapingSession =>
        `${scrapingSession.scrapingTestCaseResultCount}/${scrapingSession.scrapingTestCaseCount}`,
    },
    {
      title: 'Started by',
      dataIndex: 'createdBy',
      width: '150px',
      render: user => user?.name || 'System',
    },
    {
      title: 'Started at',
      dataIndex: 'startedAt',
      width: '120px',
      render: startedAt => formatDate(startedAt),
    },
    {
      title: 'Completed at',
      dataIndex: 'completedAt',
      width: '130px',
      render: completedAt => formatDate(completedAt),
    },
    {
      title: 'Duration',
      width: '140px',
      render: scrapingSession =>
        scrapingSession.completedAt &&
        timeBetween(scrapingSession.startedAt, scrapingSession.completedAt),
    },
    {
      title: 'Logs',
      width: '50px',
      render: scrapingSession => (
        <Button
          icon={<ExceptionOutlined />}
          onClick={event => {
            event.stopPropagation()
            setLogsModalSettings({
              sessionId: scrapingSession.id,
              isModalOpen: true,
            })
          }}
        />
      ),
    },
    {
      title: 'Rerun',
      width: '50px',
      render: renderRerunButton,
    },
    {
      title: 'Validate',
      width: '120px',
      render: scrapingSession => {
        const isValidating = isValidationInProgress(scrapingSession)

        return (
          <Button
            loading={isValidating}
            disabled={!canValidateScrapingSession(scrapingSession)}
            onClick={event =>
              handleValidateScrapingSession(event, scrapingSession)
            }
          >
            {isValidating ? 'Validating' : 'Validate'}
          </Button>
        )
      },
    },
    {
      title: 'Tag groups',
      dataIndex: 'tagGroups',
      width: '400px',
      render: tagGroups =>
        map(tagGroups, (tagGroup, index) => (
          <Collapse key={index} defaultActiveKey={['1']}>
            <Collapse.Panel header={tagGroup.alias || tagGroup.name}>
              <List
                size="small"
                split={false}
                dataSource={formatScrapingTags(tagGroup.tags)}
                itemLayout="horizontal"
                renderItem={tag => (
                  <List.Item>
                    <Typography.Text>{tag}</Typography.Text>
                  </List.Item>
                )}
              />
            </Collapse.Panel>
          </Collapse>
        )),
    },
    {
      title: 'Scraping Type',
      dataIndex: 'scrapingType',
      width: '135px',
      render: scrapingType =>
        SCRAPING_TYPES[scrapingType]?.name || scrapingType,
    },
    {
      title: 'Session Settings',
      dataIndex: 'scrapingSettings',
      width: '300px',
      render: scrapingSettings => (
        <ScrapingSessionSettingsJsonView scrapingSettings={scrapingSettings} />
      ),
    },
    {
      title: () => (
        <Popover trigger="hover" content="Refetch sessions">
          <Button
            style={{ marginRight: '15px' }}
            icon={<RetweetOutlined />}
            onClick={refreshScrapingSessions}
          />
        </Popover>
      ),
      align: 'right',
    },
  ]

  return (
    <>
      <Panel margin="small no">
        <Row gutter={24}>
          <Col span={3}>
            <ScrapingSessionStatusFilter />
          </Col>
          <Col span={3}>
            <ScrapingTypeFilter />
          </Col>
          <Col span={4}>
            <ScrapingSessionValidationStatusFilter />
          </Col>
          <Col span={10}>
            <UsersFilter />
          </Col>
        </Row>
      </Panel>
      <Table
        loading={isLoadingSessions}
        rowKey="id"
        columns={tableColumns}
        dataSource={tableSessions}
        size="small"
        scroll={{ x: 1900 }}
        onRow={scrapingSession => ({
          onClick: event =>
            event.target.tagName === 'TD'
              ? history.push(
                  `/scraping/sessions/management/${scrapingSession.id}`,
                )
              : undefined,
        })}
        pagination={{
          current: page,
          pageSize: DEFAULT_PAGE_SIZE,
          total: scrapingSessions.total,
          onChange: page => history.push(createUrl(pathname, search, { page })),
        }}
      />

      <ScrapingSessionLogsModal
        {...logsModalSettings}
        closeModal={() =>
          setLogsModalSettings({
            scrapingSessionId: undefined,
            isModalOpen: false,
          })
        }
      />
    </>
  )
}

export default ScrapingSessionsHistoryPage
